// src/scenes/TitleScene.js
// ─────────────────────────────────────────────────────────────────────────────
// Main title screen.
//   · Full artwork background (main_screen_background.png)
//   · Logo (transparent PNG) floats over the dark sky at the top
//   · Tips carousel shuffles every 3.6 s  ← edit TIPS array to set your lines
//   · "JOGAR" button at bottom with glow pulse + shimmer sweep
//
// 🔊 MUSIC: search for "MUSIC HOOK" comments — one line to uncomment per step
// ─────────────────────────────────────────────────────────────────────────────

import Phaser         from "phaser";
import logoImg        from "../assets/logo/pazuromonlogojap1.png";
import bgImg          from "../assets/backgrounds/main_screen_background.png";
import titleMusicUrl  from "../assets/audio/music/pazurumon_main_title.ogg";
import { Settings }   from "../game/Settings.js";
import { I18n }       from "../game/I18n.js";
import NameBuilderScene from "./NameBuilderScene.js";

// ── Palette (mirrors the Pazurumon logo colours) ──────────────────────────────
const GOLD      = 0xfbbf24;
const GOLD_LITE = 0xfde68a;
const LOGO_BLUE = "#1e1b8e";

export default class TitleScene extends Phaser.Scene {
  constructor() { super("TitleScene"); }

  // ── preload ───────────────────────────────────────────────────────────────
  preload() {
    this.load.image("title-logo", logoImg);
    this.load.image("title-bg",   bgImg);

    // 🔊 MUSIC — título principal
    this.load.audio("bgm_title", titleMusicUrl);
  }

  // ── create ────────────────────────────────────────────────────────────────
  create() {
    const W    = this.scale.width;   // 540
    const H    = this.scale.height;  // 960
    const TIPS = I18n.tips();

    // ── Background artwork ────────────────────────────────────────────────────
    // Fills the full canvas — villain + creatures + puzzle blocks all in one image
    this.add.image(W / 2, H / 2, "title-bg")
      .setDisplaySize(W, H)
      .setDepth(0);

    // Dark gradient at the very top so the logo text is always readable
    const topGrad = this.add.graphics().setDepth(1);
    topGrad.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.70, 0.70, 0, 0);
    topGrad.fillRect(0, 0, W, 220);

    // Dark gradient at the bottom so the button is always readable
    const botGrad = this.add.graphics().setDepth(1);
    botGrad.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0, 0.78, 0.78);
    botGrad.fillRect(0, H - 230, W, 230);

    // 🔊 Música do título em loop
    // sound.locked = browser hasn't had a user gesture yet.
    // Phaser emits 'unlocked' the moment the AudioContext is resumed (first tap anywhere).
    this.bgm = this.sound.add("bgm_title", { loop: true, volume: Settings.musicVolume });
    if (this.sound.locked) {
      this.sound.once("unlocked", () => { if (!this.bgm.isPlaying) this.bgm.play(); });
    } else {
      this.bgm.play();
    }
    this.events.once("shutdown", () => this.bgm?.stop());

    // ── Fade-in helper ────────────────────────────────────────────────────────
    const fadeIn = (targets, delay, dur = 420) =>
      this.tweens.add({ targets, alpha: 1, duration: dur, delay, ease: "Quad.Out" });

    // ── 1. LOGO ───────────────────────────────────────────────────────────────
    const LOGO_FINAL_Y = 132;
    const logo = this.add
      .image(W / 2, LOGO_FINAL_Y + 26, "title-logo")
      .setAlpha(0)
      .setDepth(2);

    // Scale logo slightly beyond full canvas width
    logo.setScale((W * 1.08) / logo.width);

    // "CHALLENGE" subtitle in Kamikaze font, directly below the logo
    const challengeY = LOGO_FINAL_Y + (logo.height * logo.scaleY) / 2 + 20;
    const challengeTxt = this.add.text(W / 2, challengeY, "CHALLENGE", {
      fontFamily: "Kamikaze, system-ui, Arial",
      fontSize: "52px",
      color: "#fbbf24",
      stroke: "#000000",
      strokeThickness: 6,
    }).setOrigin(0.5, 0).setAlpha(0).setDepth(2);

    // Entry: slide up from +26 px with Back.Out bounce
    this.tweens.add({
      targets: logo,
      alpha: 1,
      y: LOGO_FINAL_Y,
      duration: 560,
      delay: 180,
      ease: "Back.Out",
    });
    fadeIn(challengeTxt, 500, 400);

    // After entry: gentle continuous float (logo + challenge text together)
    this.time.delayedCall(900, () =>
      this.tweens.add({
        targets: [logo, challengeTxt],
        y: `-=8`,
        duration: 2600,
        yoyo: true,
        repeat: -1,
        ease: "Sine.InOut",
      })
    );

    // ── 2. TIPS CAROUSEL ──────────────────────────────────────────────────────
    // Sits between the creatures (mid) and the PLAY button (bottom)
    const TIPS_CY = 455;
    const TIPS_W  = 440;
    const TIPS_H  = 94;
    const tx = W / 2 - TIPS_W / 2;
    const ty = TIPS_CY - TIPS_H / 2;

    const tipsGfx = this.add.graphics().setAlpha(0).setDepth(2);
    tipsGfx.fillStyle(0x040810, 0.88);
    tipsGfx.fillRoundedRect(tx, ty, TIPS_W, TIPS_H, 9);
    tipsGfx.lineStyle(1, GOLD, 0.28);
    tipsGfx.strokeRoundedRect(tx, ty, TIPS_W, TIPS_H, 9);

    const tipsLabel = this.add.text(W / 2, ty + 10, I18n.t('tips_label'), {
      fontFamily: "system-ui, Arial",
      fontSize: "10px",
      color: "#fbbf24",
      fontStyle: "bold",
    }).setOrigin(0.5, 0).setAlpha(0).setDepth(2);

    this._tipIdx = 0;
    const tipText = this.add.text(W / 2, TIPS_CY - 2, TIPS[0], {
      fontFamily: "system-ui, Arial",
      fontSize: "13px",
      color: "#cbd5e1",
      align: "center",
      fontStyle: "italic",
      wordWrap: { width: TIPS_W - 32 },
      lineSpacing: 4,
    }).setOrigin(0.5).setAlpha(0).setDepth(2);

    // Dot indicators
    const DOT_Y   = ty + TIPS_H - 13;
    const DOT_GAP = 15;
    const tipDots = TIPS.map((_, i) =>
      this.add.circle(
        W / 2 + (i - (TIPS.length - 1) / 2) * DOT_GAP,
        DOT_Y, 3,
        i === 0 ? GOLD : 0x2d3f55, 1
      ).setAlpha(0).setDepth(2)
    );

    // Auto-advance with cross-fade every 3.6 s
    const advanceTip = () => {
      this.tweens.add({
        targets: tipText, alpha: 0, duration: 260,
        onComplete: () => {
          tipDots[this._tipIdx].setFillStyle(0x2d3f55, 1);
          this._tipIdx = (this._tipIdx + 1) % TIPS.length;
          tipDots[this._tipIdx].setFillStyle(GOLD, 1);
          tipText.setText(TIPS[this._tipIdx]);
          this.tweens.add({ targets: tipText, alpha: 1, duration: 260 });
        },
      });
    };
    this.time.addEvent({ delay: 3600, loop: true, callback: advanceTip });

    fadeIn([tipsGfx, tipsLabel, tipText, ...tipDots], 540, 420);

    // ── 3. PLAY BUTTON ────────────────────────────────────────────────────────
    // Positioned low — in the colourful puzzle-block zone of the artwork
    const BTN_CY = 820;
    const BTN_W  = 224;
    const BTN_H  = 60;

    // Glow orb behind button (pulses continuously)
    const btnGlow = this.add
      .circle(W / 2, BTN_CY, 100, GOLD, 0.08)
      .setAlpha(0).setDepth(2);

    // Button body (rectangle — fully supports scale tweens)
    const btnRect = this.add
      .rectangle(W / 2, BTN_CY, BTN_W, BTN_H, GOLD, 1)
      .setStrokeStyle(3, GOLD_LITE)
      .setAlpha(0).setScale(0.88).setDepth(3);

    // Button label
    const btnTxt = this.add.text(W / 2, BTN_CY, I18n.t('play_btn'), {
      fontFamily: "system-ui, Arial",
      fontSize: "23px",
      color: LOGO_BLUE,
      fontStyle: "bold",
    }).setOrigin(0.5).setAlpha(0).setScale(0.88).setDepth(4);

    // Shimmer stripe — masked white bar sweeping across the button

    const shimMask = this.make.graphics({ add: false });
    shimMask.fillStyle(0xffffff, 1);
    shimMask.fillRect(W / 2 - BTN_W / 2, BTN_CY - BTN_H / 2, BTN_W, BTN_H);

    const shimGfx = this.add.graphics().setAlpha(0).setDepth(4);
    shimGfx.fillStyle(0xffffff, 0.48);
    shimGfx.fillRect(0, -4, 20, BTN_H + 8);
    shimGfx.setMask(shimMask.createGeometryMask());
    shimGfx.setPosition(W / 2 - BTN_W / 2 - 26, BTN_CY - BTN_H / 2);

    // Invisible hit-area rectangle (always at alpha 0, on top)
    const btnHit = this.add
      .rectangle(W / 2, BTN_CY, BTN_W, BTN_H, 0xffffff, 0)
      .setDepth(5)
      .setInteractive({ useHandCursor: true });

    btnHit.on("pointerover",  () => btnRect.setAlpha(0.82));
    btnHit.on("pointerout",   () => btnRect.setAlpha(1));
    btnHit.on("pointerdown",  () =>
      this.tweens.add({
        targets: [btnRect, btnTxt],
        scaleX: 0.96, scaleY: 0.96,
        duration: 80, yoyo: true,
      })
    );
    btnHit.on("pointerup", () => {
      // 🔊 Fade de saída antes de mudar de cena
      if (this.bgm?.isPlaying) {
        this.tweens.add({
          targets: this.bgm, volume: 0, duration: 600,
          onComplete: () => { this.bgm.stop(); this._goToMap(); },
        });
      } else {
        this._goToMap();
      }
    });

    // Entry animation — pop in with Back.Out
    this.tweens.add({
      targets: [btnRect, btnTxt],
      alpha: 1, scaleX: 1, scaleY: 1,
      duration: 400, delay: 800, ease: "Back.Out",
    });
    fadeIn(shimGfx, 800, 400);

    // ── 4. ONGOING BUTTON ANIMATIONS (after all entries settle) ──────────────
    this.time.delayedCall(1220, () => {
      // Breathing pulse
      this.tweens.add({
        targets: [btnRect, btnTxt],
        scaleX: 1.035, scaleY: 1.035,
        duration: 1050, yoyo: true, repeat: -1, ease: "Sine.InOut",
      });
      // Glow pulse
      this.tweens.add({
        targets: btnGlow, alpha: 0.22,
        duration: 1000, yoyo: true, repeat: -1, ease: "Sine.InOut",
      });
      fadeIn(btnGlow, 0, 600);
      // Shimmer sweep (every ~3.5 s)
      this.tweens.add({
        targets: shimGfx,
        x: W / 2 + BTN_W / 2 + 26,
        duration: 660, ease: "Quad.InOut",
        repeat: -1, repeatDelay: 2900,
      });
    });

    // ── 5. OPTIONS button ────────────────────────────────────────────────────
    const optBtn = this.add.text(14, H - 14, I18n.t('options_btn'), {
      fontFamily: "system-ui, Arial", fontSize: "13px", color: "#64748b",
    }).setOrigin(0, 1).setAlpha(0).setDepth(2)
      .setInteractive({ useHandCursor: true });
    optBtn.on("pointerover", () => optBtn.setColor("#fbbf24"));
    optBtn.on("pointerout",  () => optBtn.setColor("#64748b"));
    optBtn.on("pointerup",   () => this.scene.launch("OptionsScene"));
    fadeIn(optBtn, 960, 700);

    // ── 6. VERSION TAG ────────────────────────────────────────────────────────
    const vTxt = this.add.text(W - 14, H - 14, "v2.5 — Early Access", {
      fontFamily: "system-ui, Arial", fontSize: "10px", color: "#475569",
    }).setOrigin(1, 1).setAlpha(0).setDepth(2);
    fadeIn(vTxt, 960, 700);

    // ── 7. LANGUAGE SELECTOR ──────────────────────────────────────────────
    // Six flag buttons — tapping sets locale and restarts the scene.
    const LOCALES = [
      { code: 'pt-PT', flag: '🇵🇹' },
      { code: 'en-US', flag: '🇬🇧' },
      { code: 'es-ES', flag: '🇪🇸' },
      { code: 'fr-FR', flag: '🇫🇷' },
      { code: 'zh-CN', flag: '🇨🇳' },
      { code: 'ja-JP', flag: '🇯🇵' },
    ];
    const flagY   = H - 14;
    const flagGap = 34;
    const flagStartX = W / 2 - ((LOCALES.length - 1) * flagGap) / 2;
    const currentLocale = I18n.getLocale();

    LOCALES.forEach(({ code, flag }, i) => {
      const fx  = flagStartX + i * flagGap;
      const isActive = code === currentLocale;
      const btn = this.add.text(fx, flagY, flag, {
        fontSize: "18px",
        alpha:    isActive ? 1 : 0.4,
      }).setOrigin(0.5, 1).setAlpha(0).setDepth(3)
        .setInteractive({ useHandCursor: true });

      btn.on('pointerover', () => btn.setAlpha(0.85));
      btn.on('pointerout',  () => btn.setAlpha(isActive ? 1 : 0.4));
      btn.on('pointerup',   () => {
        if (code === I18n.getLocale()) return;
        I18n.setLocale(code);
        this.scene.restart();
      });
      fadeIn(btn, 960, 700);
    });
  }

  _goToMap() {
    if (!NameBuilderScene.hasIdentity()) {
      this.scene.start('NameBuilderScene', { nextScene: 'MapScene', nextData: {} });
    } else {
      this.scene.start('MapScene');
    }
  }
}
